import { Injectable, OnDestroy } from '@angular/core';
import { ShortcutCode, ShortcutKey } from '../enums/keyboard-shortcut.enum';

@Injectable({
  providedIn: 'root'
})

export class KeyboardShortcutService implements OnDestroy {

  private readonly keyboardShortcutMessageType = '75F_KEYBOARD_SHORTCUT';
  private suppressNextParentShortcutRelay = false;

  // Handles the keydown event for the service.
  private readonly keydownHandler = (event: KeyboardEvent): void => {
    this.onKeydown(event);
  };

  // Initializes the keyboard event listeners for the service.
  initializeKeyboardEvents(): void {
    window.addEventListener('keydown', this.keydownHandler);
  }

  /**
   * Intercepts keyboard navigation and shortcut events within the iframe
   * and forwards host-owned shortcuts to the parent shell.
   */
  private onKeydown(event: KeyboardEvent): void {
    // Do not echo the synthetic keydown created from a parent shortcut message.
    if (this.suppressNextParentShortcutRelay) {
      this.suppressNextParentShortcutRelay = false;
      return;
    }

    const target = event.target as HTMLElement;

    const isInput =
      target &&
      (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      );

    const isMatSelectPanelOpen = !!document.querySelector(
      '.mat-select-panel, .mat-mdc-select-panel'
    );

    // Allow normal typing and interaction with open mat-select.
    if (isInput || isMatSelectPanelOpen) {
      return;
    }

    const isTab =
      event.key === ShortcutKey.TAB ||
      event.code === ShortcutCode.TAB;

    const isEscape =
      event.key === ShortcutKey.ESCAPE ||
      event.code === ShortcutCode.ESCAPE;

    const isHelp =
      event.key === ShortcutKey.QUESTION_MARK ||
      (event.shiftKey &&
        (event.key === ShortcutKey.SLASH || event.code === ShortcutCode.SLASH));

    const isChatShortcut =
      event.key.toLowerCase() === 's' &&
      !event.shiftKey &&
      !event.ctrlKey &&
      !event.altKey &&
      !event.metaKey;

    // Host-owned shortcuts.
    if (isTab || isEscape || isHelp || isChatShortcut) {
      event.preventDefault();
      this.postShortcutToParent(event);
      return;
    }

    // Other application shortcut candidates.
    if (this.shouldBridgeKeyboardEvent(event)) {
      this.postShortcutToParent(event);
    }
  }

  // Sends an iframe keydown to the parent shell using the shared shortcut bridge message format.
  private postShortcutToParent(event: KeyboardEvent): void {
    if (window.parent === window) {
      return;
    }
    window.parent.postMessage(JSON.stringify({
      type: this.keyboardShortcutMessageType,
      key: event.key,
      code: event.code,
      shiftKey: !!event.shiftKey,
      altKey: !!event.altKey,
      ctrlKey: !!event.ctrlKey,
      metaKey: !!event.metaKey,
      plain: !event.shiftKey && !event.altKey && !event.ctrlKey && !event.metaKey,
      source: 'IFRAME',
      sequence: `IFRAME-${Date.now()}-${Math.random()}`
    }), '*');
  }

  // Keeps normal typing and browser-reserved shortcuts out of the iframe bridge.
  private shouldBridgeKeyboardEvent(event: KeyboardEvent): boolean {
    if (event.defaultPrevented || event.ctrlKey || event.altKey || event.metaKey || event.isComposing) {
      return false;
    }
    // Relay app shortcut candidates only: navigation keys plus plain letters/digits.
    return event.key === ShortcutKey.ESCAPE
      || event.key === ShortcutKey.TAB
      || event.key === ShortcutKey.QUESTION_MARK
      || event.code === ShortcutCode.SLASH
      || event.code?.startsWith('Key')
      || event.code?.startsWith('Digit');
  }

  // Removes the keyboard event listeners for the service.
  removeKeyboardEvents(): void {
    window.removeEventListener('keydown', this.keydownHandler);
  }

  // Cleans up the keyboard event listeners when the service is destroyed.
  ngOnDestroy(): void {
    this.removeKeyboardEvents();
  }
}